import React, { useEffect, useState } from 'react';
import { captureAPI } from '../services/api';
import { useIsAdmin } from '../identity/useIsAdmin';
import './CaptureSettingsPage.css';

export default function CaptureSettingsPage() {
  const isAdmin = useIsAdmin();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await captureAPI.getCaptureSettings();
      if (res?.ok) {
        setSettings(res.settings);
      } else {
        setError('Failed to load settings');
      }
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (field, value) => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await captureAPI.updateCaptureSettings({ [field]: value });
      if (res?.ok) {
        setSettings(res.settings);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        setError('Failed to save settings');
      }
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRetentionChange = async (value) => {
    const retentionDays = value === '' ? null : parseInt(value, 10);
    setSaving(true);
    setSaved(false);
    try {
      const res = await captureAPI.updateCaptureSettings({ retention_days: retentionDays });
      if (res?.ok) {
        setSettings(res.settings);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        setError('Failed to save retention setting');
      }
    } catch (err) {
      setError('Failed to save retention setting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cs-page">
        <div className="cs-loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="cs-page">
      <div className="cs-header">
        <h2>Capture Settings</h2>
        <p className="cs-subtitle">
          Configure default extraction behavior for all documents. These settings can be overridden per vendor in Vendor Profiles.
        </p>
      </div>

      {error && <div className="cs-error">{error}</div>}

      {/* Cluster 2 / A1 + A9: capture settings are tenant-wide config — all
          cards (extraction, retention, AI behavior) are admin-only. Non-admins
          see a single muted note. (Slice 2 role-based pivot: AI behavior is now
          admin-only too, so the note returns to its generic all-settings form.) */}
      {!isAdmin && (
        <div className="cs-card">
          <p className="cs-setting-description" style={{ margin: 0 }}>
            Capture settings are managed by your tenant admin.
          </p>
        </div>
      )}

      {isAdmin && (<>
      <div className="cs-card">
        <h3 className="cs-card-title">Extraction Settings</h3>
        
        <div className="cs-setting">
          <div className="cs-setting-main">
            <label className="cs-toggle-label">
              <input
                type="checkbox"
                className="cs-toggle-input"
                checked={settings?.extract_line_items || false}
                onChange={(e) => handleToggle('extract_line_items', e.target.checked)}
                disabled={saving}
              />
              <span className="cs-toggle-switch"></span>
              <span className="cs-toggle-text">Extract line items</span>
            </label>
            {saved && <span className="cs-saved-indicator">✓ Saved</span>}
          </div>
          <p className="cs-setting-description">
            Extract line items from invoices and purchase orders. When enabled, the system will attempt to extract individual line items from documents. Can be overridden per vendor in Vendor Profiles.
          </p>
        </div>
      </div>

      <div className="cs-card" style={{ marginTop: 20 }}>
        <h3 className="cs-card-title">Document Retention</h3>

        <div className="cs-setting">
          <div className="cs-setting-main">
            <div className="cs-select-label">
              <span className="cs-toggle-text">Retention period</span>
              <select
                className="cs-select"
                value={settings?.retention_days != null ? String(settings.retention_days) : ''}
                onChange={(e) => handleRetentionChange(e.target.value)}
                disabled={saving}
              >
                <option value="">Never (keep indefinitely)</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
              </select>
            </div>
            {saved && <span className="cs-saved-indicator">&#10003; Saved</span>}
          </div>
          <p className="cs-setting-description">
            Automatically purge published and archived documents after the selected period. Documents still in review or processing are never affected. Individual documents can be exempted using the "Keep indefinitely" option in the Processing Monitor.
          </p>
        </div>
      </div>

      {/* Cluster 5 / A8 pt 7 (Slice 2, role-based pivot): tenant default for
          Pathway D (Claude no-match fallback). ADMIN-ONLY — sits inside the
          admin wrapper alongside the other capture-config cards; members do not
          see it (consistent with extraction / retention). */}
      <div className="cs-card" style={{ marginTop: 20 }}>
        <h3 className="cs-card-title">AI behavior</h3>

        <div className="cs-setting">
          <div className="cs-setting-main">
            <label className="cs-toggle-label">
              <input
                type="checkbox"
                className="cs-toggle-input"
                checked={settings?.default_pathway_d_enabled ?? true}
                onChange={(e) => handleToggle('default_pathway_d_enabled', e.target.checked)}
                disabled={saving}
              />
              <span className="cs-toggle-switch"></span>
              <span className="cs-toggle-text">Claude AI fallback (Pathway D) for new workflows</span>
            </label>
            {saved && <span className="cs-saved-indicator">&#10003; Saved</span>}
          </div>
          <p className="cs-setting-description">
            Default for newly created workflows: when a document matches no vendor profile and no template,
            Claude AI extracts it (Pathway D). Each workflow can override this in its own settings; changing
            this default does not affect existing workflows.
          </p>
        </div>
      </div>
      </>)}
    </div>
  );
}
